import { useEffect } from "react";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SWRConfig } from "swr";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { Stack } from '@mui/material';
import AppRoutes from "./components/global/AppRoutes";
import { fetcher } from "./utils/fetcher";
import { ThemeProvider } from "./contexts/ThemeContext";
import { TransactionProvider } from "./contexts/TransactionContext";
import { OnboardingProvider } from "./contexts/OnboardingContext";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  // Subscribe to query/mutation caches inside useEffect so we can clean up
  useEffect(() => {
    const unsubscribeQueryCache = queryClient.getQueryCache().subscribe((event) => {
      if (event?.query?.state?.error) {
        const error = event.query.state.error;
        console.error('React Query Error:', error);
        Sentry.captureException(error, {
          tags: {
            errorType: 'ReactQuery',
          },
          contexts: {
            reactQuery: {
              queryKey: String(event.query.queryKey),
              error: error?.message || String(error),
            },
          },
        });
      }
    });

    const unsubscribeMutationCache = queryClient.getMutationCache().subscribe((event) => {
      if (event?.mutation?.state?.status === 'error' && event.mutation.state.error) {
        const error = event.mutation.state.error;
        console.error('React Query Mutation Error:', error);
        Sentry.captureException(error, {
          tags: {
            errorType: 'ReactQueryMutation',
          },
          contexts: {
            reactQuery: {
              error: error?.message || String(error),
            },
          },
        });
      }
    });

    return () => {
      unsubscribeQueryCache();
      unsubscribeMutationCache();
    };
  }, []);

  useEffect(() => {
    let rafId: number;

    const updateScreenHeight = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const height = window.visualViewport?.height ?? window.innerHeight;
        document.documentElement.style.setProperty(
          "--screen-height",
          `${height}px`
        );
      });
    };

    // Set initial value
    updateScreenHeight();

    // Listen on visualViewport for smoother mobile address bar changes
    const viewport = window.visualViewport;
    if (viewport) {
      viewport.addEventListener("resize", updateScreenHeight);
    }
    window.addEventListener("resize", updateScreenHeight);

    return () => {
      cancelAnimationFrame(rafId);
      if (viewport) {
        viewport.removeEventListener("resize", updateScreenHeight);
      }
      window.removeEventListener("resize", updateScreenHeight);
    };
  }, []);

  return (
    <Sentry.ErrorBoundary
      fallback={
        <Stack height="var(--screen-height)" justifyContent="center" alignItems="center">
          Đã xảy ra lỗi / Something went wrong
        </Stack>
      }
      onError={(error, errorInfo) => {
        console.error('ErrorBoundary caught an error:', error);
        console.error('Error info:', errorInfo);
        const componentStack = typeof errorInfo === 'object' && 'componentStack' in errorInfo
          ? (errorInfo as { componentStack?: string }).componentStack
          : undefined;
        Sentry.captureException(error, {
          contexts: {
            react: {
              componentStack: componentStack || String(errorInfo),
            },
          },
        });
      }}
    >
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <TransactionProvider>
            <SWRConfig
              value={{
                fetcher,
                revalidateOnFocus: false,
                revalidateOnReconnect: false,
                dedupingInterval: 10000,
                errorRetryCount: 3,
                errorRetryInterval: 5000,
                focusThrottleInterval: 5000,
                refreshInterval: 0,
                onError: (error, key) => {
                  console.error('SWR Error:', error, 'for key:', key);
                  Sentry.captureException(error, {
                    tags: {
                      errorType: 'SWR',
                      swrKey: String(key),
                    },
                    contexts: {
                      swr: {
                        key: String(key),
                        error: error?.message || String(error),
                      },
                    },
                  });
                },
              }}
            >
              <OnboardingProvider>
                <AppRoutes />
                <ToastContainer hideProgressBar />
              </OnboardingProvider>
            </SWRConfig>
          </TransactionProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default Sentry.withProfiler(App);
